import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Lock, Eye, Users, FileCheck, Globe, ChevronRight, Calendar, Bot, Cpu, Zap, Network } from 'lucide-react'

const HomePage = () => {
  const navigate = useNavigate()

  const complianceBadges = [
    { name: 'SOC 2 Type II', icon: Shield, status: 'Verified', year: '2024' },
    { name: 'ISO 27001', icon: Lock, status: 'Certified', year: '2024' },
    { name: 'GDPR', icon: Eye, status: 'Compliant', year: 'Ongoing' },
    { name: 'HIPAA', icon: Users, status: 'Ready', year: '2024' },
  ]

  const securityFeatures = [
    {
      title: 'End-to-End Encryption',
      description: 'All data is encrypted in transit and at rest using industry-standard AES-256 encryption.',
      icon: Lock
    },
    {
      title: 'Access Controls',
      description: 'Role-based access control with time-boxed permissions and audit logging.',
      icon: Users
    },
    {
      title: 'Compliance Monitoring',
      description: 'Continuous monitoring and reporting for SOC 2, ISO 27001, and other frameworks.',
      icon: FileCheck
    },
    {
      title: 'Global Infrastructure',
      description: 'Secure, distributed infrastructure with data residency controls.',
      icon: Globe
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">TrustCenterPage.com</h1>
            </div>
            <div className="flex items-center gap-6">
              <a href="/directory" className="text-gray-700 hover:text-primary-600 font-medium">Directory</a>
              <a href="/pricing" className="text-gray-700 hover:text-primary-600 font-medium">Pricing</a>
              <a href="/admin" className="text-gray-700 hover:text-primary-600 font-medium">Company Login</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl">
              Trust for Humans
              <span className="block text-primary-600">and Machines.</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              The trust center platform built for the AI era. Human reviewers get compliance docs, NDA workflows,
              and audit reports. AI systems get machine-readable APIs, cryptographic proofs, and agent security scanning.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://calendar.app.google/mtvgJVaXPtfD95dcA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <Calendar className="mr-2 h-5 w-5" />
                Book a Demo
              </a>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center px-6 py-3 border border-primary-600 text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Sign In to Your Trust Center
                <ChevronRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Built for Everyone Who Needs to Verify Trust</h2>
            <p className="text-lg text-gray-600">Whether you're a human reviewer or an automated system, trust should be verifiable.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Human Teams */}
            <div className="bg-primary-50 rounded-xl p-8 border border-primary-100">
              <div className="flex items-center mb-6">
                <Users className="h-8 w-8 text-primary-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">For Human Teams</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Compliance docs, audit reports & certificates',
                  'NDA workflows with 5 professional templates',
                  'AI-powered CAIQ v4.0.3 knowledge base (261 questions)',
                  'CRM-connected deal acceleration',
                  'SOC 2, ISO 27001, GDPR, HIPAA attestations',
                  'Version control with preview/live/rollback',
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-sm text-gray-700">
                    <Shield className="h-4 w-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {/* AI Systems */}
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-700">
              <div className="flex items-center mb-6">
                <Bot className="h-8 w-8 text-blue-400 mr-3" />
                <h3 className="text-xl font-bold text-white">For AI Systems</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Machine-readable REST APIs for automated compliance checks',
                  'Zero-knowledge proofs of skill integrity (PLONK)',
                  'AI agent security scanning — OWASP LLM Top 10',
                  'Lethal trifecta detection (private data + untrusted input + exfiltration)',
                  'Cryptographic rank & attestation badges',
                  'OpenTelemetry-compatible security telemetry export',
                ].map((item, i) => (
                  <li key={i} className="flex items-start text-sm text-gray-300">
                    <Cpu className="h-4 w-4 text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Badges */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Certifications</h2>
            <p className="text-lg text-gray-600 mb-12">Industry-recognized security and compliance standards</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {complianceBadges.map((badge, index) => {
              const IconComponent = badge.icon
              return (
                <div key={index} className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200 hover:border-primary-300 transition-colors">
                  <IconComponent className="h-12 w-12 text-primary-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{badge.name}</h3>
                  <p className="text-sm text-gray-600 mb-1">{badge.status}</p>
                  <p className="text-xs text-gray-500">{badge.year}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Security Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Security Features</h2>
            <p className="text-lg text-gray-600">Enterprise-grade security built into every layer</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {securityFeatures.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <div className="flex items-start">
                    <IconComponent className="h-6 w-6 text-primary-600 mt-1 mr-4 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Subprocessors Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Subprocessors</h2>
            <p className="text-lg text-gray-600 mb-8">Third-party vendors we work with to deliver our services</p>
            <div className="bg-gray-50 rounded-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
                <div className="border-b border-gray-200 pb-4 md:border-b-0 md:border-r md:pr-6">
                  <h4 className="font-semibold text-gray-900">Cloud Infrastructure</h4>
                  <p className="text-sm text-gray-600 mt-1">AWS, Google Cloud</p>
                  <p className="text-xs text-gray-500 mt-1">Data processing & storage</p>
                </div>
                <div className="border-b border-gray-200 pb-4 md:border-b-0 lg:border-r lg:pr-6">
                  <h4 className="font-semibold text-gray-900">Monitoring & Analytics</h4>
                  <p className="text-sm text-gray-600 mt-1">DataDog, Segment</p>
                  <p className="text-xs text-gray-500 mt-1">System monitoring & analytics</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Communication</h4>
                  <p className="text-sm text-gray-600 mt-1">SendGrid, Twilio</p>
                  <p className="text-xs text-gray-500 mt-1">Email & SMS delivery</p>
                </div>
              </div>
              <div className="mt-8 text-center">
                <a href="/subprocessors" className="text-primary-600 hover:text-primary-700 font-medium">
                  View Full Subprocessor List →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Need Access to Detailed Evidence?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Get access to our complete security documentation, audit reports,
            and compliance certificates under NDA.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Sign In or Request Access
            <ChevronRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-primary-400" />
                <span className="ml-2 text-lg font-semibold">TrustCenterPage.com</span>
              </div>
              <p className="text-gray-400">
                Trust for humans and machines. Compliance docs, AI agent security,
                and cryptographic attestation — all from one platform.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="/status" className="hover:text-white">Status Page</a></li>
                <li><a href="/privacy" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white">Terms of Service</a></li>
                <li><a href="/dpa" className="hover:text-white">Data Processing Agreement</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Security Team: security@company.com</li>
                <li>Privacy Team: privacy@company.com</li>
                <li>Support: support@company.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Company Name. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default HomePage